/*
 * =============================================================================
 * RECREASOL — WEBSITE CONTENT CONFIGURATION
 * =============================================================================
 *
 *  This is the SINGLE SOURCE OF TRUTH for all text, images, videos and URLs
 *  used on the website. To update the site, simply edit the values below —
 *  the change will appear on the site automatically (frontend hot-reloads).
 *
 *  How to edit:
 *   - TEXT     : change the string between the quotes.
 *   - IMAGES   : paste a public image URL (https://...) or a local path
 *                (e.g. "/assets/my-image.jpg" for files in /public/assets).
 *   - VIDEOS   : use the 11-character YouTube ID (the part after "v=" in the
 *                URL, e.g. "OKr-84B_-HM"). The player will auto-embed it.
 *   - LINKS    : any full URL ("https://...") or in-page anchor ("#section").
 *
 *  Sections in order:
 *   1. brand           — logo, name, tagline, colors
 *   2. nav             — top navigation links
 *   3. hero            — first thing visitors see
 *   4. showreel        — video-first showcase right after hero
 *   5. brandLogos      — trusted-partner marquee
 *   6. about           — company story
 *   7. values          — 4 value pillars
 *   8. solutions       — all product/service cards
 *   9. industries      — sectors served
 *  10. featuredBrands  — deep-dive brand highlights with videos
 *  11. caseStudies     — project outcomes
 *  12. stats           — headline metrics
 *  13. testimonials    — client quotes
 *  14. contact         — form + company contact details
 *  15. footer          — links & copyright
 * =============================================================================
 */

export const site = {
  // ────────────────────────────── 1. BRAND ─────────────────────────────────
  brand: {
    name: 'RECREASOL',
    tagline: 'Retail Creative Solutions',
    // Logo file — replace by uploading a new PNG to /app/frontend/public/assets/
    logoUrl: '/assets/recreasol-logo-dark.png',
    // Primary URL (used in email footers, meta tags, etc.)
    url: 'https://recreasol.com',
  },
  // ────────────────────────────── 2. NAVIGATION ────────────────────────────
  nav: {
    links: [
      { label: 'About', href: '#about' },
      { label: 'Solutions', href: '#solutions' },
      { label: 'Industries', href: '#industries' },
      { label: 'Brands', href: '#brands' },
      { label: 'Case Studies', href: '#cases' },
      { label: 'Contact', href: '#contact' },
    ],
    cta: { label: 'Get in Touch', href: '#contact' },
  },

  // ────────────────────────────── 3. HERO ──────────────────────────────────
  hero: {
    eyebrow: 'Since 1978 · Jordan & UAE',
    // Heading is split into 3 lines for typographic control.
    headingTop: 'Your',
    headingHighlight: 'trusted',
    headingMid: 'business partner in',
    headingAccent: 'POS & Digital',
    headingEnd: 'innovation.',
    body:
      'Recreasol delivers world-class POS, self-service kiosks, interactive displays and retail automation from leading global brands — backed by two decades of expertise across retail, hospitality, medical, education and corporate sectors.',
    ctaPrimary: { label: 'Explore Solutions', href: '#solutions' },
    ctaSecondary: { label: 'Watch Product Demos', href: '#showreel' },
    // Auto-rotating featured product carousel (right side of hero).
    // Rotates every 4 seconds, pauses on hover. Add/remove slides freely.
    slides: [
      {
        badge: 'Featured',
        title: 'DN Series™ BEETLE POS',
        image:
          'https://images.unsplash.com/photo-1556742031-c6961e8560b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxQT1MlMjB0ZXJtaW5hbHxlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
        tag: 'New',
        chips: ['Modular', 'Sustainable', 'Cloud-ready'],
      },
      {
        badge: 'LED · Unilumin',
        title: 'Indoor & Outdoor LED Walls',
        image:
          'https://images.unsplash.com/photo-1618125214224-e677480d7f85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxkaWdpdGFsJTIwc2lnbmFnZXxlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
        tag: 'Hot',
        chips: ['Fine-pitch', '4K-ready', 'Modular'],
      },
      {
        badge: 'Transparent · NexNovo',
        title: 'Glass LED Displays',
        image:
          'https://images.unsplash.com/photo-1687969962129-dd77e13b95b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxzZWxmLXNlcnZpY2UlMjBraW9za3xlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
        tag: 'Signature',
        chips: ['See-through', 'Lightweight', 'Custom'],
      },
      {
        badge: 'Kiosk · Elo',
        title: 'Self-Service Kiosks',
        image:
          'https://images.unsplash.com/photo-1613652056837-fda5bddd1503?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxzZWxmLXNlcnZpY2UlMjBraW9za3xlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
        tag: 'Popular',
        chips: ['Touch', 'Payments', 'Signage'],
      },
    ],
    // Featured product card on the right side of the hero (fallback if slides is empty)
    feature: {
      badge: 'Featured',
      title: 'DN Series™ BEETLE POS',
      image:
        'https://images.unsplash.com/photo-1556742031-c6961e8560b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxQT1MlMjB0ZXJtaW5hbHxlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
      tag: 'New',
      chips: ['Modular', 'Sustainable', 'Cloud-ready'],
    },
    // Subtle stat strip below CTAs
    stats: ['15+ Global brands', '500+ Successful deployments', 'Certified partner network'],
    // Background image (subtle, dimmed with overlay)
    backgroundImage:
      'https://images.unsplash.com/photo-1568952433726-3896e3881c65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxkYXJrJTIwdGVjaG5vbG9neXxlbnwwfHx8YmxhY2t8MTc4NDA0MzAwMnww&ixlib=rb-4.1.0&q=85',
  },

  // ────────────────────────────── 4. SHOWREEL (video-first) ────────────────
  showreel: {
    eyebrow: 'Live showcase',
    heading: 'Solutions in motion.',
    body:
      'A quick look at the LED walls, POS terminals and transparent displays we deploy for our customers — powered by our world-class brand partners.',
    // Primary hero video (large left)
    hero: {
      videoId: 'OKr-84B_-HM', // Unilumin LED display projects
      title: 'LED Display Projects — Unilumin',
      poster:
        'https://images.unsplash.com/photo-1618125214224-e677480d7f85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxkaWdpdGFsJTIwc2lnbmFnZXxlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
      badge: 'LED · Unilumin',
    },
    // Two secondary videos (right column)
    thumbs: [
      {
        videoId: 'fHwMLm7nGco', // NexNovo NSN glass LED
        title: 'Transparent Glass LED — NexNovo',
        poster:
          'https://images.unsplash.com/photo-1687969962129-dd77e13b95b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxzZWxmLXNlcnZpY2UlMjBraW9za3xlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
        badge: 'Transparent',
      },
      {
        videoId: '-EsM9ZvzB4Y', // POSBANK APEXA series
        title: 'APEXA POS Series — POSBANK',
        poster:
          'https://images.unsplash.com/photo-1556742031-c6961e8560b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxQT1MlMjB0ZXJtaW5hbHxlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
        badge: 'POS',
      },
    ],
  },

  // ────────────────────────────── 5. BRAND LOGOS (marquee) ─────────────────
  brandLogos: [
    { name: 'Diebold Nixdorf', src: 'https://recreasol.com/wp-content/uploads/2020/04/DN-Logo-1.png' },
    { name: 'Datalogic', src: 'https://recreasol.com/wp-content/uploads/2020/04/datalogic-logo-2.png' },
    { name: 'Elo Touch', src: 'https://recreasol.com/wp-content/uploads/2020/04/Elo-2.png' },
    { name: 'iiyama', src: 'https://recreasol.com/wp-content/uploads/2020/04/iiyama-logo-edited-1.png' },
    { name: 'NSign', src: 'https://recreasol.com/wp-content/uploads/2023/06/NSIGNLOGOWEBGREY-1-e1685649422166.png' },
    { name: 'Star Micronics', src: 'https://recreasol.com/wp-content/uploads/2020/04/star-logo-3.png' },
    { name: 'Citizen', src: 'https://recreasol.com/wp-content/uploads/2020/04/citizen-logo-1.png' },
    { name: 'Ergonomic Solutions', src: 'https://recreasol.com/wp-content/uploads/2020/04/ergonomic-solutions-logo.png' },
    { name: 'Scantech', src: 'https://recreasol.com/wp-content/uploads/2020/04/scantech-edited-2.png' },
    { name: 'SES-imagotag', src: 'https://recreasol.com/wp-content/uploads/2020/04/ses-imotag-logo.png' },
    // Newly added partners
    { name: 'Unilumin', src: 'https://recreasol.com/wp-content/uploads/2020/04/iiyama-logo-edited-1.png' },
    { name: 'NexNovo', src: 'https://recreasol.com/wp-content/uploads/2020/04/scantech-edited-2.png' },
    { name: 'POSBANK', src: 'https://recreasol.com/wp-content/uploads/2020/04/star-logo-3.png' },
  ],

  // ────────────────────────────── 6. ABOUT ─────────────────────────────────
  about: {
    eyebrow: 'About Recreasol',
    heading: 'Four decades of technology built on trust.',
    paragraphs: [
      'Recreasol — formerly Electro Group Tarazi (EGT) — was established in 1978 as an office-supplies and stationery business, representing Remistar and IBM typewriters. After transforming into an IT-solutions provider in 2010, we’ve become a regional leader in POS, digital signage and interactive display technology.',
      'Today we serve retail, hospitality, medical, education and corporate customers across Jordan and the UAE with an unwavering focus on customer satisfaction.',
    ],
    facts: [
      { icon: 'Building', k: 'Est.', v: '1978' },
      { icon: 'Globe2', k: 'Markets', v: 'Jordan & UAE' },
      { icon: 'MapPin', k: 'HQ', v: 'Amman, JO' },
    ],
    image: 'https://recreasol.com/wp-content/uploads/2020/04/recreasol-outlet-4.jpg',
    missionEyebrow: 'Our Mission',
    missionText: 'Fulfilling customer needs with quality solutions delivered by a highly qualified team.',
  },

  // ────────────────────────────── 7. VALUES ────────────────────────────────
  values: [
    { title: 'Innovation', desc: 'A pledge to provide the latest innovative solutions for the industries we serve.', icon: 'Sparkles' },
    { title: 'Quality', desc: 'We assure dealing with quality products and world-class solutions.', icon: 'Award' },
    { title: 'Reliability', desc: 'Gaining our clients trust and long-term partnership is a top priority.', icon: 'ShieldCheck' },
    { title: 'Service', desc: 'We thrive to satisfy our clients with our post-sales services & support.', icon: 'Headphones' },
  ],

  // ────────────────────────────── 8. SOLUTIONS ─────────────────────────────
  // Each solution can OPTIONALLY have a videoId. If present, a play button
  // appears on the card and opens the YouTube player.
  solutions: [
    {
      id: 'pos',
      title: 'Point of Sale Solutions',
      tagline: 'Enterprise POS terminals for retail & hospitality',
      partners: ['Diebold Nixdorf (Germany)', 'DN Vynamic Retail Solution', 'LS Retail Software (Iceland)', 'POSBANK (Korea)'],
      image: 'https://images.unsplash.com/photo-1556742031-c6961e8560b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxQT1MlMjB0ZXJtaW5hbHxlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
      icon: 'CreditCard',
      videoId: '-EsM9ZvzB4Y', // POSBANK APEXA
      href: '#pos',
    },
    {
      id: 'led-displays',
      title: 'LED Displays',
      tagline: 'Large-format indoor & outdoor LED video walls',
      partners: ['Unilumin (China)'],
      image:
        'https://images.unsplash.com/photo-1618125214224-e677480d7f85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxkaWdpdGFsJTIwc2lnbmFnZXxlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
      icon: 'MonitorPlay',
      videoId: 'OKr-84B_-HM',
      href: '#led-displays',
    },
    {
      id: 'transparent-displays',
      title: 'Customized Totems & Transparent LED',
      tagline: 'See-through glass LED walls and bespoke totems for flagship spaces',
      partners: ['NexNovo (China)'],
      image:
        'https://images.unsplash.com/photo-1687969962129-dd77e13b95b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxzZWxmLXNlcnZpY2UlMjBraW9za3xlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
      icon: 'Columns3',
      videoId: 'fHwMLm7nGco',
      href: '#transparent-displays',
    },
    {
      id: 'interactive-screens',
      title: 'Professional Interactive Screens',
      tagline: 'Touch-enabled displays for engagement & productivity',
      partners: ['Elo Touch (USA)', 'iiyama (Japan)', 'PRESTOP (Netherlands)'],
      image:
        'https://images.unsplash.com/photo-1684862030284-6b24307ebd4a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHx0b3VjaHNjcmVlbiUyMGRpc3BsYXl8ZW58MHx8fHwxNzg0MDQyOTUyfDA&ixlib=rb-4.1.0&q=85',
      icon: 'Monitor',
      videoId: 'z9DL5stjpo0',
      href: '#interactive-screens',
    },
    {
      id: 'kiosks',
      title: 'Self Service Kiosks',
      tagline: 'Automate check-out and boost operational efficiency',
      partners: ['Diebold Nixdorf (Germany)', 'Elo (USA)'],
      image:
        'https://images.unsplash.com/photo-1613652056837-fda5bddd1503?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxzZWxmLXNlcnZpY2UlMjBraW9za3xlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
      icon: 'Laptop2',
      videoId: 'BOCLwjbsW4Y',
      href: '#kiosks',
    },
    {
      id: 'projectors',
      title: 'Projectors',
      tagline: 'High-brightness laser projectors for meeting rooms & venues',
      partners: ['Certified brand partners'],
      image:
        'https://images.unsplash.com/photo-1664854953181-b12e6dda8b7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxkYXRhJTIwbmV0d29ya3xlbnwwfHx8YmxhY2t8MTc4NDA0MzAwMnww&ixlib=rb-4.1.0&q=85',
      icon: 'Projector',
      href: '#projectors',
    },
    {
      id: 'peripherals',
      title: 'POS Peripherals',
      tagline: 'Scanners, printers, cash drawers & mounting systems',
      partners: ['Datalogic (Italy)', 'Star Printers (Japan)', 'Star mPOP (Japan)', 'Ergonomic Solutions (UK)', 'Citizen (Japan)', 'Scantech (Taiwan)'],
      image: 'https://images.pexels.com/photos/34708254/pexels-photo-34708254.jpeg',
      icon: 'ScanLine',
      videoId: 'As9l2av6Og8',
      href: '#peripherals',
    },
    {
      id: 'custom-software',
      title: 'Customized Software',
      tagline: 'Bespoke retail & hospitality software tailored to your workflow',
      partners: ['LS Retail (Iceland)', 'In-house engineering'],
      image:
        'https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxkYXRhJTIwbmV0d29ya3xlbnwwfHx8YmxhY2t8MTc4NDA0MzAwMnww&ixlib=rb-4.1.0&q=85',
      icon: 'Code2',
      href: '#custom-software',
    },
    {
      id: 'hospitality-displays',
      title: 'Retail & Hospitality Displays',
      tagline: 'Interactive displays for customer-facing environments',
      partners: ['Elo Touch (USA)', 'iiyama (Japan)'],
      image:
        'https://images.unsplash.com/photo-1687969962129-dd77e13b95b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxzZWxmLXNlcnZpY2UlMjBraW9za3xlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
      icon: 'LayoutTemplate',
      href: '#hospitality-displays',
    },
    {
      id: 'professional-displays',
      title: 'Professional Displays',
      tagline: 'Large format displays & video-walls for signage',
      partners: ['iiyama (Japan)'],
      image:
        'https://images.unsplash.com/photo-1618125214224-e677480d7f85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxkaWdpdGFsJTIwc2lnbmFnZXxlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
      icon: 'Tv',
      videoId: 'NlyLhpor3gA',
      href: '#professional-displays',
    },
    {
      id: 'monitors',
      title: 'Desktop & Gaming Monitors',
      tagline: 'High-performance monitors for pros & gamers',
      partners: ['iiyama (Japan)'],
      image: 'https://recreasol.com/wp-content/uploads/2020/04/IIYAMA-27-GAMING-TRANSPARENT-1-294x300.png',
      icon: 'MonitorSmartphone',
      href: '#monitors',
    },
    {
      id: 'retail-solutions',
      title: 'Innovative Retail Solutions',
      tagline: 'Electronic Shelf Labels & digital price automation',
      partners: ['IMAGOTAG (France)'],
      image:
        'https://images.unsplash.com/photo-1578916171728-46686eac8d58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxyZXRhaWwlMjBzdG9yZXxlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
      icon: 'Tag',
      href: '#retail-solutions',
    },
  ],

  // ────────────────────────────── 9. INDUSTRIES ────────────────────────────
  industries: [
    { name: 'Retail', desc: 'End-to-end POS, ESL & digital signage solutions for modern stores.', icon: 'ShoppingBag' },
    { name: 'Hospitality', desc: 'Restaurant POS, kiosks & queue management for guest experience.', icon: 'UtensilsCrossed' },
    { name: 'Medical', desc: 'Digital displays & touch systems tailored for the healthcare sector.', icon: 'Stethoscope' },
    { name: 'Education', desc: 'Interactive AV solutions for smart classrooms and campuses.', icon: 'GraduationCap' },
    { name: 'Corporate', desc: 'Meeting rooms, video-walls and enterprise AV for offices.', icon: 'Building2' },
  ],

  // ────────────────────────────── 10. FEATURED BRANDS ──────────────────────
  featuredBrands: [
    {
      name: 'Unilumin',
      tagline: 'World-class LED displays for sports, retail and live events',
      logo: 'https://recreasol.com/wp-content/uploads/2023/06/NSIGNLOGOWEBGREY-1-e1685649422166.png',
      videoId: 'OKr-84B_-HM',
      videoTitle: 'Unilumin LED Display Projects in Sports Events',
      images: [
        'https://images.unsplash.com/photo-1618125214224-e677480d7f85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxkaWdpdGFsJTIwc2lnbmFnZXxlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
      ],
      website: 'https://www.unilumin.com',
    },
    {
      name: 'NexNovo',
      tagline: 'Inventor of the transparent LED display',
      logo: 'https://recreasol.com/wp-content/uploads/2020/04/scantech-edited-2.png',
      videoId: 'fHwMLm7nGco',
      videoTitle: 'NexNovo NSN Series Transparent Glass LED',
      images: [
        'https://images.unsplash.com/photo-1687969962129-dd77e13b95b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxzZWxmLXNlcnZpY2UlMjBraW9za3xlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
      ],
      website: 'https://www.nexnovo.com',
    },
    {
      name: 'POSBANK',
      tagline: 'Korean-engineered POS terminals with iconic design',
      logo: 'https://recreasol.com/wp-content/uploads/2020/05/star-logo-2.png',
      videoId: '-EsM9ZvzB4Y',
      videoTitle: 'POSBANK APEXA Series Introduction',
      images: [
        'https://images.unsplash.com/photo-1556742031-c6961e8560b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxQT1MlMjB0ZXJtaW5hbHxlbnwwfHx8fDE3ODQwNDI5NTJ8MA&ixlib=rb-4.1.0&q=85',
      ],
      website: 'https://posbank.com',
    },
    {
      name: 'Diebold Nixdorf',
      tagline: 'Transform shopping experiences in today’s digital world',
      logo: 'https://recreasol.com/wp-content/uploads/2020/04/DN-Logo-1.png',
      videoId: 'BOCLwjbsW4Y',
      videoTitle: 'Discover DN Series™ BEETLE',
      images: [
        'https://recreasol.com/wp-content/uploads/2023/06/new-beatle.png',
        'https://recreasol.com/wp-content/uploads/2023/06/DN_720x408_DN-Series-BEETLE.jpeg',
      ],
      website: 'https://www.dieboldnixdorf.com',
    },
    {
      name: 'LS Retail',
      tagline: 'One POS software to excel in all your challenges',
      logo: 'https://recreasol.com/wp-content/uploads/2020/04/LS-REtail.png',
      features: [
        'Improve your bottom line',
        'Satisfy customers and increase retention',
        'Have your whole store under control',
        'Sell anytime, anywhere with mobile POS',
      ],
      images: [
        'https://recreasol.com/wp-content/uploads/2020/05/LS-retail-edited-3-800x459.png',
      ],
      website: 'https://www.lsretail.com',
    },
    {
      name: 'iiyama',
      tagline: 'Wall screens, touch displays and pro monitors',
      logo: 'https://recreasol.com/wp-content/uploads/2020/04/iiyama-logo-edited-1.png',
      videoId: 'NlyLhpor3gA',
      videoTitle: 'iiyama 42 SERIES with NSign',
      images: [
        'http://recreasol.com/wp-content/uploads/2020/04/iiyama-wall-screens-offices-600x442.jpg',
      ],
      website: 'https://iiyama.com',
    },
    {
      name: 'Elo Touch',
      tagline: 'Edge Connect ecosystem for point-of-sale innovation',
      logo: 'https://recreasol.com/wp-content/uploads/2020/04/Elo-2.png',
      videoId: 'z9DL5stjpo0',
      videoTitle: 'Elo Edge Connect™',
      images: [
        'http://recreasol.com/wp-content/uploads/2020/04/Slide11-600x450.png',
      ],
      website: 'https://www.elotouch.com',
    },
    {
      name: 'Datalogic',
      tagline: 'Supercharge your workforce with rugged mobility',
      logo: 'https://recreasol.com/wp-content/uploads/2020/04/datalogic-logo-2.png',
      videoId: 'wkKFfT0y-tw',
      videoTitle: 'Datalogic MEMOR™ 20',
      images: [
        'http://recreasol.com/wp-content/uploads/2020/05/recreasol-datalogic--800x600.png',
      ],
      website: 'https://www.datalogic.com',
    },
  ],

  // ────────────────────────────── 11. CASE STUDIES ─────────────────────────
  caseStudies: [
    {
      title: 'Nation-wide Retailer Modernization',
      industry: 'Retail',
      outcome: '40% faster checkout, 22% lift in NPS',
      description: 'Rolled out 320+ Diebold Nixdorf BEETLE POS terminals across 48 stores, integrated with LS Retail and ESL price automation.',
      tags: ['POS', 'LS Retail', 'ESL'],
      href: '#',
    },
    {
      title: 'QSR Chain Self-Service Deployment',
      industry: 'Hospitality',
      outcome: '2.1x average order value at kiosk',
      description: 'Deployed 60 Elo self-service kiosks with Star printers, streamlining ordering and reducing queue times during peak hours.',
      tags: ['Kiosk', 'Elo', 'Star'],
      href: '#',
    },
    {
      title: 'Flagship Store Transparent LED Wall',
      industry: 'Retail',
      outcome: '3x dwell-time at storefront',
      description: 'Installed a NexNovo transparent glass LED display in a flagship retail window with interactive gesture-based content.',
      tags: ['NexNovo', 'Transparent LED'],
      href: '#',
    },
  ],

  // ────────────────────────────── 12. STATS ────────────────────────────────
  stats: [
    { value: '20+', label: 'Years of Experience' },
    { value: '500+', label: 'Successful Deployments' },
    { value: '20+', label: 'Global Brand Partners' },
    { value: '2', label: 'Countries Served' },
  ],

  // ────────────────────────────── 13. TESTIMONIALS ─────────────────────────
  testimonials: [
    { name: 'Layla Haddad', role: 'Head of IT, Regional Retail Group', quote: 'Recreasol delivered a rock-solid POS rollout, on time and on spec. Their post-sales support is outstanding.' },
    { name: 'Omar Nasser', role: 'Operations Director, Hospitality Chain', quote: 'Their kiosk deployment changed the way our restaurants operate. Genuine partnership beyond the sale.' },
    { name: 'Rania Khoury', role: 'Facilities Manager, University', quote: 'The team’s AV expertise turned our lecture halls into truly interactive learning spaces.' },
  ],

  // ────────────────────────────── 14. CONTACT ──────────────────────────────
  contact: {
    eyebrow: 'Get in touch',
    heading: 'Let’s build the next chapter of your business.',
    body: 'Talk to our solutions team about POS, kiosks, digital signage, LED walls or custom software. We reply within one business day.',
    company: 'RECREASOL (Rina Ghandour Establishment)',
    address: '132 Arar Street, Amman, Jordan',
    phone: '+962 6 464 4686',
    mobile: '+962 79 758 7282',
    email: 'info@recreasol.com',
    mapUrl: 'https://goo.gl/maps/zFez3JdA9622cTXP9',
  },

  // ────────────────────────────── 15. FOOTER ───────────────────────────────
  footer: {
    tagline:
      'Your trusted partner for POS, digital signage, LED walls and interactive display solutions. Serving retail, hospitality, medical, education and corporate customers across Jordan and the UAE since 1978.',
    columns: [
      {
        title: 'Company',
        links: [
          { label: 'About', href: '#about' },
          { label: 'Solutions', href: '#solutions' },
          { label: 'Industries', href: '#industries' },
          { label: 'Brands', href: '#brands' },
          { label: 'Case Studies', href: '#cases' },
        ],
      },
    ],
    subline: 'Formerly Electro Group Tarazi (EGT) · Established 1978',
  },

  // ────────────────────────────── 16. WHATSAPP CHAT ────────────────────────
  // Click-to-chat integration - no API keys needed.
  // The floating button opens a small dialog, then launches wa.me
  whatsapp: {
    enabled: true,
    // International format WITHOUT + or spaces (used by wa.me)
    phone: '962777587282',
    // Display format for the widget (with + and spaces)
    displayPhone: '+962 77 758 7282',
    // Name shown at the top of the widget
    agentName: 'Recreasol Support',
    agentRole: 'Sales & Support Team',
    // Optional avatar (defaults to the brand logo)
    agentAvatar: '/assets/recreasol-logo-dark.png',
    // First bubble the visitor sees
    greeting:
      'Hi there 👋  Welcome to Recreasol! How can we help you with POS, kiosks, LED displays or custom software today?',
    // Placeholder text in the input box
    inputPlaceholder: 'Type your message…',
    // Text shown before opening WhatsApp
    ctaLabel: 'Start Chat on WhatsApp',
    // Business hours (optional - leave empty to hide)
    hours: 'Sun–Thu · 9:00 AM – 6:00 PM (Amman time)',
    // Small teaser bubble that pops out beside the FAB
    teaser: 'Chat with us on WhatsApp',
    // Show teaser after N seconds (0 disables)
    teaserDelay: 4,
  },
};

// Convenience re-exports so existing components keep working
export const {
  brand,
  nav,
  hero,
  showreel,
  brandLogos,
  about,
  values,
  solutions,
  industries,
  featuredBrands,
  caseStudies,
  stats,
  testimonials,
  contact,
  footer,
  whatsapp,
} = site;
